import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Switch, Chip, Typography, Box, TextField, Tooltip, CircularProgress, Button,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Home from './Home';
import AlerteRegleDialog from './AlerteRegleDialog';
import AlerteJournalDialog from './AlerteJournalDialog';
import {
  AlerteRegle, EtatMoteur, StatutRegle, DernierePasse,
  listerAlertesRegles, etatMoteurAlertes, majAlerteRegle, supprimerAlerteRegle, lancerPasseAlertes,
  parseTitres, resumerCible, resumerMotif, texteDeclenchement
} from '../services/alertesReglesServices';

// « 2026-09-14 10:00:00 » (DATE_FORMAT MySQL) ou ISO → « 14/09/2026 10:00 »
const formatDateFr = (s: string | null | undefined): string => {
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
  return s;
};

const statutChip: Record<StatutRegle, { label: string; color: 'success' | 'default' | 'warning' | 'error'; variant?: 'outlined' }> = {
  envoye: { label: 'Envoyé', color: 'success' },
  vide: { label: 'Rien à envoyer', color: 'default' },
  dry_run: { label: 'Simulation', color: 'warning' },
  echec_template: { label: 'Template désactivé', color: 'error' },
  echec_envoi: { label: "Échec d'envoi", color: 'error' },
  erreur: { label: 'Erreur', color: 'error' },
  jamais: { label: 'Jamais évaluée', color: 'default', variant: 'outlined' },
};

const resumePasse = (p: DernierePasse): string =>
  `${formatDateFr(p.date)} (${p.mode === 'dry_run' ? 'simulation' : 'réel'}, ${p.declencheur}) : ${p.nb_regles} règle(s), `
  + `${p.nb_dossiers_scannes} dossier(s) scanné(s), ${p.nb_candidats} candidat(s), ${p.nb_envoyes} mail(s) envoyé(s), `
  + `${p.nb_echecs} échec(s)${p.nb_template_inactif ? `, ${p.nb_template_inactif} template inactif` : ''} — ${p.duree_ms} ms`;

// Message d'une erreur axios (400 : { message, erreurs: [] } ; 503 : { message })
const messageErreur = (e: unknown, defaut: string): string => {
  if (axios.isAxiosError(e) && e.response && e.response.data) {
    const data = e.response.data as { message?: string; erreurs?: string[] };
    if (Array.isArray(data.erreurs) && data.erreurs.length) return data.erreurs.join(' ');
    if (data.message) return data.message;
  }
  return defaut;
};

const AlertesScreen: React.FC = () => {
  const [regles, setRegles] = useState<AlerteRegle[]>([]);
  const [etat, setEtat] = useState<EtatMoteur | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AlerteRegle | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [journalRegle, setJournalRegle] = useState<AlerteRegle | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AlerteRegle | null>(null);
  const [confirmPasse, setConfirmPasse] = useState(false);
  const [passeEnCours, setPasseEnCours] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  // Les deux appels sont indépendants : un 503 « tables absentes » sur la liste ne doit pas priver de /etat (bannière rouge)
  const fetchAll = async () => {
    setLoading(true);
    const [r, e] = await Promise.allSettled([listerAlertesRegles(), etatMoteurAlertes()]);
    if (e.status === 'fulfilled') setEtat(e.value);
    else console.error('Erreur chargement état moteur', e.reason);
    if (r.status === 'fulfilled') setRegles(r.value);
    else {
      console.error('Erreur chargement règles', r.reason);
      setRegles([]);
      // pas de toast redondant si la bannière rouge explique déjà
      if (e.status !== 'fulfilled' || e.value.tables_ok) setToast({ msg: 'Chargement des règles impossible', sev: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return regles.filter(
      (r) => r.libelle.toLowerCase().includes(q) || resumerCible(r).toLowerCase().includes(q)
    );
  }, [regles, search]);

  const toggleActif = async (r: AlerteRegle, actif: boolean) => {
    // maj optimiste
    setRegles((prev) => prev.map((x) => (x.id === r.id ? { ...x, actif: actif ? 1 : 0 } : x)));
    try {
      await majAlerteRegle(r.id, { actif });
    } catch (e) {
      console.error('Erreur toggle actif', e);
      setRegles((prev) => prev.map((x) => (x.id === r.id ? { ...x, actif: actif ? 0 : 1 } : x)));
      setToast({ msg: messageErreur(e, 'Modification impossible'), sev: 'error' });
    }
  };

  const openEdit = (r: AlerteRegle | null) => {
    setSelected(r);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const r = confirmDelete;
    setConfirmDelete(null);
    try {
      await supprimerAlerteRegle(r.id);
      setToast({ msg: `Règle « ${r.libelle} » supprimée`, sev: 'success' });
      fetchAll();
    } catch (e) {
      console.error('Erreur suppression règle', e);
      setToast({ msg: messageErreur(e, 'Suppression impossible'), sev: 'error' });
    }
  };

  const handlePasse = async () => {
    setConfirmPasse(false);
    setPasseEnCours(true);
    try {
      const p = await lancerPasseAlertes();
      setToast({ msg: `Passe terminée — ${resumePasse(p)}`, sev: p.mode === 'dry_run' ? 'warning' : 'success' });
    } catch (e) {
      console.error('Erreur passe manuelle', e);
      setToast({ msg: messageErreur(e, 'Lancement de la passe impossible'), sev: 'error' });
    } finally {
      setPasseEnCours(false);
      fetchAll();
    }
  };

  const dryRun = !etat || etat.dry_run;

  return (
    <>
      <Home />
      <Container maxWidth={false} sx={{ mt: 2, px: { xs: 1, sm: 3 } }}>
        {/* Bannière d'état du moteur */}
        {!loading && !etat && (
          <Alert severity="error" sx={{ mb: 2 }}>
            État du moteur d'alertes indisponible (backend injoignable ?).
          </Alert>
        )}
        {etat && !etat.tables_ok && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Alertes désactivées : {etat.raison_desactivation || 'tables absentes'}. Jouer la migration
            migrations/create_alertes_regles.sql ; le moteur se réactive seul dès que les tables existent.
          </Alert>
        )}
        {etat && etat.tables_ok && etat.dry_run && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            MODE SIMULATION — aucun mail n'est envoyé (source : {etat.source_flag}).
            {etat.derniere_passe ? ` Dernière passe : ${resumePasse(etat.derniere_passe)}` : ' Aucune passe depuis le démarrage.'}
          </Alert>
        )}
        {etat && etat.tables_ok && !etat.dry_run && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Envois réels actifs — dernière passe :{' '}
            {etat.derniere_passe ? resumePasse(etat.derniere_passe) : 'aucune depuis le démarrage'}
          </Alert>
        )}
        {etat && etat.tables_ok && !etat.template.present && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Template « alerte_visa_depart » absent en base : jouer <code>node scripts/seedEmailTemplates.js</code>
            {' '}(le contenu du registry sert de repli en attendant).
          </Alert>
        )}
        {etat && etat.template.present && etat.template.actif === 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" component={Link} to="/mails">Ouvrir Mails</Button>}
          >
            Le mail « Alerte interne — dossiers au départ proche » est désactivé dans Mails : aucune alerte ne partira.
          </Alert>
        )}
        {etat && (
          <Typography
            variant="caption"
            display="block"
            sx={{ mb: 2, color: Math.abs(etat.decalage_secondes || 0) > 300 ? 'warning.main' : 'text.secondary' }}
          >
            {etat.mysql_jour
              ? `Jour MySQL : ${etat.mysql_jour} (${etat.tz_mysql || '?'}) — décalage Node/MySQL : ${etat.decalage_secondes === null ? '?' : etat.decalage_secondes} s`
              : 'Horloge MySQL illisible'}
            {' '}— {etat.nb_regles_actives} règle(s) active(s){etat.passe_en_cours ? ' — passe en cours' : ''}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Alertes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Règles d'alerte automatique “départ proche” : 1 mail récapitulatif par règle et par passe horaire vers
            contact@visamonde.com, une seule fois par dossier.
          </Typography>
          <TextField
            label="Rechercher"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEdit(null)}>
            Nouvelle règle
          </Button>
          <Button
            variant="outlined"
            startIcon={passeEnCours ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            disabled={passeEnCours || !etat || !etat.tables_ok}
            onClick={() => setConfirmPasse(true)}
          >
            Lancer une passe maintenant
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Règle</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cible</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Concernés</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Dernier envoi</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Dernière passe</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actif</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => {
                  const c = r.compteurs || { nb_concernes: 0, nb_a_envoyer: 0, nb_envoyes_total: 0, nb_echecs: 0 };
                  const titres = parseTitres(r.visas_titres);
                  const motif = resumerMotif(r);
                  const chip = statutChip[r.dernier_statut] || statutChip.jamais;
                  return (
                    <TableRow key={r.id} hover sx={{ opacity: Number(r.actif) === 1 ? 1 : 0.6 }}>
                      <TableCell sx={{ fontWeight: 500, minWidth: 180 }}>
                        {r.libelle}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {texteDeclenchement(r)}
                          {Number(r.ecard_non_faite) === 1 ? ' · seulement si e-card non faite' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {titres.map((t) => (
                            <Chip key={t} label={t} size="small" variant="outlined" />
                          ))}
                          {motif && (
                            <Chip label={`motif : ${motif}`} size="small" sx={{ bgcolor: '#e0e0e0' }} />
                          )}
                          {titres.length === 0 && !motif && (
                            <Typography variant="caption" color="error">aucune cible</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {c.nb_concernes === 0 ? (
                          <Tooltip title="Aucun dossier actif ne correspond : vérifier le ciblage (visa renommé ?)">
                            <Typography component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>0</Typography>
                          </Tooltip>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: 'bold' }}>{c.nb_concernes}</Typography>
                        )}
                        {c.nb_a_envoyer > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: 'warning.main' }}>
                            dont {c.nb_a_envoyer} à alerter à la prochaine passe
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.dernier_envoi ? formatDateFr(r.dernier_envoi) : 'jamais'}
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: c.nb_echecs > 0 ? 'error.main' : 'text.secondary' }}
                        >
                          {c.nb_envoyes_total} envoyé(s) · {c.nb_echecs} en échec
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            <>
                              {r.derniere_evaluation ? `Évaluée le ${formatDateFr(r.derniere_evaluation)}` : 'Jamais évaluée'}
                              {r.dernier_detail ? <><br />{r.dernier_detail}</> : null}
                            </>
                          }
                        >
                          <Chip label={chip.label} size="small" color={chip.color} variant={chip.variant} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={Number(r.actif) === 1 ? 'Règle active' : 'Règle en pause'}>
                          <Switch
                            size="small"
                            checked={Number(r.actif) === 1}
                            onChange={(e) => toggleActif(r, e.target.checked)}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Modifier">
                          <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Journal des envois">
                          <IconButton size="small" onClick={() => setJournalRegle(r)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(r)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucune règle
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <AlerteRegleDialog
        open={dialogOpen}
        regle={selected}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchAll}
      />
      <AlerteJournalDialog
        open={!!journalRegle}
        regle={journalRegle}
        onClose={() => setJournalRegle(null)}
      />

      {/* Confirmation de suppression */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmez la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer la règle « {confirmDelete ? confirmDelete.libelle : ''} » ? Elle sera désactivée et masquée ;
            l'historique des dossiers déjà alertés est conservé. Une règle recréée à l'identique ré-alertera les
            dossiers dans sa fenêtre.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">Annuler</Button>
          <Button onClick={handleDelete} color="error" autoFocus>Confirmer</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation de passe manuelle */}
      <Dialog open={confirmPasse} onClose={() => setConfirmPasse(false)}>
        <DialogTitle>Lancer une passe maintenant ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dryRun
              ? 'Mode simulation : rien ne sera envoyé.'
              : 'Les dossiers à alerter recevront un mail réel (une seule fois par dossier).'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPasse(false)} color="primary">Annuler</Button>
          <Button onClick={handlePasse} variant="contained" color={dryRun ? 'primary' : 'warning'} autoFocus>
            Lancer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default AlertesScreen;
